import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Search, Plus, Eye, Edit, UserPlus, Activity, Calendar } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const PatientManagement = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user.role !== 'doctor') {
      toast.error('Only doctors can access patient management');
      navigate('/dashboard');
      return;
    }
    loadPatients();
    loadStats();
  }, []);

  const loadPatients = async (search = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/patients`, {
        params: { search, status: 'active' }
      });
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/patients/stats/overview`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      loadPatients(value);
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-8 sm:py-8 pt-24 " data-testid="patient-management-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('nav.patientManagement')}</h1>
            <p className="text-gray-600 text-lg">{t('patientManagement.subtitle')}</p>
          </div>
          <Button
            onClick={() => navigate('/patients/add')}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
            data-testid="add-patient-btn"
          >
            <UserPlus className="w-5 h-5" />
            Add New Patient
          </Button>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-10 h-10" />
                <span className="text-4xl font-bold">{stats.total_patients}</span>
              </div>
              <p className="text-sm opacity-90">Total Patients</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-cyan-500 to-teal-600 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-10 h-10" />
                <span className="text-4xl font-bold">{stats.male_patients}</span>
              </div>
              <p className="text-sm opacity-90">Male Patients</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-teal-600 to-cyan-700 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-10 h-10" />
                <span className="text-4xl font-bold">{stats.female_patients}</span>
              </div>
              <p className="text-sm opacity-90">Female Patients</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-cyan-600 to-teal-700 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Calendar className="w-10 h-10" />
                <span className="text-4xl font-bold">{stats.today_appointments}</span>
              </div>
              <p className="text-sm opacity-90">Today's Appointments</p>
            </Card>
          </div>
        )}

        {/* Search Bar */}
        <Card className="p-6 bg-white shadow-lg mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search patients by name, email, or phone..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 py-3 text-lg"
              data-testid="patient-search-input"
            />
          </div>
        </Card>

        {/* Patient List */}
        <Card className="p-6 bg-white shadow-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Patient List</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading patients...</p>
            </div>
          ) : patients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No patients found matching your search' : 'No patients yet'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => navigate('/patients/add')}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Patient
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-bold text-gray-700">Patient Name</th>
                    <th className="text-left py-4 px-4 font-bold text-gray-700">Age / Gender</th>
                    <th className="text-left py-4 px-4 font-bold text-gray-700">Contact</th>
                    <th className="text-left py-4 px-4 font-bold text-gray-700">Blood Group</th>
                    <th className="text-left py-4 px-4 font-bold text-gray-700">Last Updated</th>
                    <th className="text-left py-4 px-4 font-bold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr key={patient.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
                            <span className="text-teal-700 font-bold">
                              {patient.full_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">{patient.full_name}</p>
                            <p className="text-sm text-gray-500">{patient.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-gray-800">{calculateAge(patient.date_of_birth)} years</p>
                        <p className="text-sm text-gray-500 capitalize">{patient.gender}</p>
                      </td>
                      <td className="py-4 px-4 text-gray-800">
                        {patient.phone}
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                          {patient.blood_group || 'N/A'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-600 text-sm">
                        {new Date(patient.updated_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/patients/${patient.id}`)}
                            className="flex items-center gap-1"
                            data-testid={`view-patient-${patient.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/patients/${patient.id}/edit`)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PatientManagement;
